import axios from 'axios';
import { i18n } from 'vue-lang-router';
import authHeader from '../../services/auth.header';
import router from '../../router';

const apiUrl = process.env.VUE_APP_API_SUPPORT_URL;

const getInitialState = () => {
  return {
    comments: null,
    postedComments: [],
    loading: false,
    loadingMore: false,
    reachedTheEnd: false,
    perPage: 10,
    page: 1,
    totalPages: Number,
    totalItems: Number,
    error: '',
  };
};

export default {
  state: {
    comments: getInitialState(),
    highlitedComments: getInitialState(),
  },
  getters: {
    comments: (state) => state.comments.comments,
    postedComments: (state) => state.comments.postedComments,
    loading: (state) => state.comments.loading,
    loadingMore: (state) => state.comments.loadingMore,
    reachedTheEnd: (state) => state.comments.reachedTheEnd,
    totalItems: (state) => state.comments.totalItems,

    highlightedComments: (state) => state.highlitedComments.comments,
    highlightedCommentsLoading: (state) => state.highlitedComments.loading,
    highlightedCommentsLoadingMore: (state) => state.highlitedComments.loadingMore,
    highlightedCommentsReachedTheEnd: (state) => state.highlitedComments.reachedTheEnd,
    highlightedCommentsTotalItems: (state) => state.highlitedComments.totalItems,
  },
  mutations: {
    getComments: (state, payload) => {
      if (payload.loadingMore) {
        state.comments.loadingMore = true;
      } else {
        state.comments.loading = true;
      }
      if (state.comments.totalPages > state.comments.page) {
        state.comments.page += 1;
      }
    },
    getCommentsSuccess: (state, payload) => {
      if (state.comments.loadingMore) {
        state.comments.comments.push(...payload.data);
        state.comments.loadingMore = false;
      } else {
        state.comments.comments = payload.data;
        state.comments.totalItems = Number(payload.headers['x-wp-total']);
        state.comments.totalPages = Number(payload.headers['x-wp-totalpages']);
        state.comments.loading = false;
      }
      if ((state.comments.totalPages === state.comments.page) || !state.comments.totalPages) {
        state.comments.reachedTheEnd = true;
      }
    },
    getCommentsFailure: (state) => {
      state.comments.loading = false;
      state.comments.loadingMore = false;
      state.comments.error = 'Error';
    },
    getHighlightedComments: (state, payload) => {
      if (payload.loadingMore) {
        state.highlitedComments.loadingMore = true;
      } else {
        state.highlitedComments.loading = true;
      }
      if (state.highlitedComments.totalPages > state.highlitedComments.page) {
        state.highlitedComments.page += 1;
      }
    },
    getHighlightedCommentsSuccess: (state, payload) => {
      if (state.highlitedComments.loadingMore) {
        state.highlitedComments.comments.push(...payload.data);
        state.highlitedComments.loadingMore = false;
      } else {
        state.highlitedComments.comments = payload.data;
        state.highlitedComments.totalItems = Number(payload.headers['x-wp-total']);
        state.highlitedComments.totalPages = Number(payload.headers['x-wp-totalpages']);
        state.highlitedComments.loading = false;
      }
      if ((state.highlitedComments.totalPages === state.highlitedComments.page) || !state.highlitedComments.totalPages) {
        state.highlitedComments.reachedTheEnd = true;
      }
    },
    getHighlightedCommentsFailure: (state) => {
      state.highlitedComments.loading = false;
      state.highlitedComments.loadingMore = false;
      state.highlitedComments.error = 'Error';
    },
    sendComment: (state) => {
      state.comments.error = '';
    },
    sendCommentSuccess: (state, payload) => {
      if (payload.status === 'approved') {
        const comment = payload;
        comment.liked = false;
        comment.likes_count = 0;
        state.comments.postedComments.unshift(payload);
      }
    },
    sendCommentFailure: (state) => {
      state.comments.error = i18n.t('comments.failedToSendComment');
    },
    likeCommentSuccess: (state, payload) => {
      const comment = state.comments.comments.find((el) => el.id === payload.commentId)
                      || state.highlitedComments.comments.find((el) => el.id === payload.commentId)
                      || state.comments.postedComments.find((el) => el.id === payload.commentId);
      if (comment) {
        comment.liked = true;
        comment.likes_count = payload.likesCount;
      }
    },
    dislikeCommentSuccess: (state, payload) => {
      const comment = state.comments.comments.find((el) => el.id === payload.commentId)
                      || state.highlitedComments.comments.find((el) => el.id === payload.commentId)
                      || state.comments.postedComments.find((el) => el.id === payload.commentId);
      if (comment) {
        comment.liked = false;
        comment.likes_count = payload.likesCount;
      }
    },
    resetState: (state) => {
      Object.assign(state.comments, getInitialState());
      Object.assign(state.highlitedComments, getInitialState());
      state.error = '';
    },
  },
  actions: {
    GET_COMMENTS: ({ commit, state }, payload) => {
      const { loadingMore } = payload;
      commit('getComments', { loadingMore });
      const params = {
        post: payload.materialId,
        per_page: state.comments.perPage,
        page: state.comments.page,
      };
      if (state.comments.postedComments.length) {
        let excludeParam = '';
        state.comments.postedComments.forEach((comment, i) => {
          if (i === 0) {
            excludeParam += `${comment.id}`;
          } else {
            excludeParam += `,${comment.id}`;
          }
        });
        params.exclude = excludeParam;
      }
      return new Promise((resolve, reject) => {
        axios.get(`${apiUrl}/comments`, { params, headers: authHeader() })
          .then(({ data, headers }) => {
            commit('getCommentsSuccess', { data, headers });
            resolve();
          })
          .catch(() => {
            commit('getCommentsFailure');
            reject();
          });
      });
    },
    GET_HIGHLIGHTED_COMMENTS: ({ commit, state }, payload) => {
      const { loadingMore } = payload;
      commit('getHighlightedComments', { loadingMore });
      const params = {
        post: payload.materialId,
        per_page: state.highlitedComments.perPage,
        page: state.highlitedComments.page,
      };
      return new Promise((resolve, reject) => {
        axios.get(`${apiUrl}/highlighted-comments`, { params, headers: authHeader() })
          .then(({ data, headers }) => {
            commit('getHighlightedCommentsSuccess', { data, headers });
            resolve();
          })
          .catch(() => {
            commit('getHighlightedCommentsFailure');
            reject();
          });
      });
    },
    SEND_COMMENT: ({ commit, dispatch }, payload) => {
      commit('sendComment');
      const params = {
        post: payload.materialId,
        content: payload.comment,
        author_name: payload?.author,
        author_email: payload?.email,
      };
      return new Promise((resolve, reject) => {
        axios.post(`${process.env.VUE_APP_API_URL}/comments`, params, { headers: authHeader() })
          .then((response) => {
            commit('sendCommentSuccess', response.data);
            resolve(response.data);
          })
          .catch(({ response }) => {
            let errorMessage = '';
            if (response.data.code === 'comment_duplicate') {
              errorMessage = i18n.t('comments.duplicateCommentDetected');
            } else if (response.data.code === 'comment_flood') {
              errorMessage = i18n.t('comments.postingCommentsTooQuickly');
            } else if (response.data.data?.errorCode === 14) {
              // Expired token
              dispatch('user/LOGOUT', '', { root: true });
              errorMessage = i18n.t('auth.sessionExpired');
            } else {
              errorMessage = i18n.t('common.connectionError');
            }
            reject(errorMessage);
            commit('sendCommentFailure');
          });
      });
    },
    LIKE_COMMENT: ({ commit, dispatch }, payload) => {
      const params = {
        comment_id: payload,
      };
      return new Promise((resolve, reject) => {
        axios.post(`${apiUrl}/like-comment`, params, { headers: authHeader() })
          .then((response) => {
            const data = {
              commentId: payload,
              likesCount: response.data.likes_count,
            };
            commit('likeCommentSuccess', data);
            resolve();
          })
          .catch(({ response }) => {
            if (response) {
              if (response.data.data?.errorCode === 14) {
                dispatch('user/LOGOUT', '', { root: true });
                router.push('/login?session_expired=1');
              } else {
                reject(i18n.t('common.connectionError'));
              }
            }
          });
      });
    },
    DISLIKE_COMMENT: ({ commit, dispatch }, payload) => {
      const params = {
        comment_id: payload,
      };
      return new Promise((resolve, reject) => {
        axios.post(`${apiUrl}/dislike-comment`, params, { headers: authHeader() })
          .then((response) => {
            const data = {
              commentId: payload,
              likesCount: response.data.likes_count,
            };
            commit('dislikeCommentSuccess', data);
            resolve();
          })
          .catch(({ response }) => {
            if (response) {
              if (response.data.data?.errorCode === 14) {
                dispatch('user/LOGOUT', '', { root: true });
                router.push('/login?session_expired=1');
              } else {
                reject(i18n.t('common.connectionError'));
              }
            }
          });
      });
    },
    RESET_STATE: ({ commit }) => {
      commit('resetState');
    },
  },
  namespaced: true,
};
